import React, { useState } from 'react';

import { useRouteFocus } from '../hooks/useRouteFocus';

/**
 * Mounted once inside the router (App.js, beside PageTrackingMount /
 * ScrollRestorationMount). Renders the app's single polite live region — the
 * "route announcer" — and drives it via useRouteFocus: on each in-app
 * navigation the new page's <h1> text is written here (announced to screen
 * readers) and focus is moved to the new page's <main> (bug row 122).
 *
 * The live region must PERSIST across route changes (a fresh element per route
 * would never register as a change for assistive tech), which is exactly why it
 * lives on this always-mounted component rather than inside any page.
 */

// Visually hidden but present in the accessibility tree (the sr-only clip
// pattern — NOT display:none, which would remove it from the a11y tree and mute
// the announcement).
const srOnly: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export function RouteFocusMount(): React.ReactElement {
  const [message, setMessage] = useState('');
  useRouteFocus(setMessage);
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid="route-announcer"
      style={srOnly}
    >
      {message}
    </div>
  );
}

export default RouteFocusMount;
